import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import Alert from '@pelckmans/elementis/components/alert';
import AlertGroup from '@pelckmans/elementis/components/alert-group';

import { Module } from '../../api/module';
import IdentityContext from '../../contexts/IdentityContext';
import useLicenses from '../../hooks/useLicenses';
import { getRoleTranslationKey } from '../../pages/material/toc/components/utils';
import ModuleLicenseAlert from './ModuleLicenseAlert';

const TRANSLATION_KEY = 'SHARED_COMPONENTS.ALERTS.SINGLE';

interface Props {
  module: Module;
}

export default function ModuleAlerts({ module }: Props) {
  const { t } = useTranslation();
  const { identity } = useContext(IdentityContext);
  const { getUserLicenseValidationType } = useLicenses();

  const alerts = [];

  const licenseValidationType = getUserLicenseValidationType(module);

  if (licenseValidationType !== 'LICENSED') {
    alerts.push(
      <ModuleLicenseAlert key="module-license-alert" message={t(`${TRANSLATION_KEY}.${licenseValidationType}`)} />,
    );
  }

  if (['preview', 'review'].includes(module.publishingState)) {
    alerts.push(
      <Alert key={module.publishingState} prominence="info">
        {t(`${TRANSLATION_KEY}.${module.publishingState.toUpperCase()}`, {
          role: t(getRoleTranslationKey(identity.highestRole, identity.isAuthor)),
        })}
      </Alert>,
    );
  } else if (module.publishingState === 'phasedOut') {
    alerts.push(
      <Alert key={module.publishingState} prominence="info">
        {t(`${TRANSLATION_KEY}.PHASEDOUT${identity.isTeacherAlike ? '' : '_STUDENT'}`)}
      </Alert>,
    );
  }

  if (module.incomplete) {
    alerts.push(
      <Alert key="module-incomplete-alert" prominence="info">
        {t(`${TRANSLATION_KEY}.INCOMPLETE`)}
      </Alert>,
    );
  }

  if (alerts.length === 0) {
    return null;
  }

  return alerts.length === 1 ? (
    alerts
  ) : (
    <AlertGroup aria-label={undefined} role="group">
      {alerts}
    </AlertGroup>
  );
}
